import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { useMsal } from '@azure/msal-react';

type FlowKind = 'implicit' | 'hybrid';

interface RawOAuthTesterProps {
  clientId?: string;
  tenant?: string; // 'common' | 'organizations' | tenant id | domain
  defaultScopes?: string; // e.g., "openid profile User.Read"
  defaultRedirect?: string; // e.g., `${origin}/raw-oauth-callback.html`
  onBack?: () => void;
}

function randomString(len = 32): string {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => ('0' + b.toString(16)).slice(-2)).join('');
}

// Base64url encoding (no padding, URL-safe)
function base64urlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Generate PKCE code_verifier (random 43-128 character string)
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array.buffer);
}

// Generate PKCE code_challenge from code_verifier
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(digest);
}

function buildAuthorizeUrl(params: Record<string, string>) {
  const q = new URLSearchParams(params);
  const base = params.__authBase__;
  // remove internal param
  q.delete('__authBase__');
  return `${base}/oauth2/v2.0/authorize?${q.toString()}`;
}

function decodeJwtPart(part: string) {
  try {
    const payload = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Minimal error handling: actual error details will show in the popup.

const RawOAuthTester: React.FC<RawOAuthTesterProps> = ({
  clientId,
  tenant = 'organizations',
  defaultScopes = 'openid profile', // was "openid profile User.Read"
  defaultRedirect,
  onBack,
}) => {
  const { accounts } = useMsal();
  const origin = window.location.origin;
  
  // Get current tenant from MSAL if available
  const currentTenantId = accounts.length > 0 ? accounts[0].tenantId : null;
  
  const defaultRedirectUri = useMemo(
    () => defaultRedirect || `${origin}/raw-oauth-callback.html`,
    [origin, defaultRedirect]
  );

  // Enhanced tenant options with current tenant pre-selected
  const tenantOptions = [
    { value: 'common', label: 'common (Multi-tenant + Personal)' },
    { value: 'organizations', label: 'organizations (Multi-tenant)' },
    ...(currentTenantId ? [{ value: currentTenantId, label: `${currentTenantId} (Current tenant)` }] : []),
  ];

  // Initialize form with current tenant if available
  const [form, setForm] = useState({
    clientId: clientId || '',
    tenant: currentTenantId || tenant,
    redirectUri: defaultRedirectUri,
    scopes: defaultScopes,
  });

  const [stateVal, setStateVal] = useState(randomString(16));
  const [nonceVal, setNonceVal] = useState(randomString(16));
  const [flowType, setFlowType] = useState<FlowKind>('implicit');

  // PKCE state for hybrid flow
  const [codeVerifier, setCodeVerifier] = useState('');
  const [codeChallenge, setCodeChallenge] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [exchangeResult, setExchangeResult] = useState<any>(null);

  const [lastUrl, setLastUrl] = useState('');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [copyOk, setCopyOk] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const popupRef = useRef<Window | null>(null);

  // Parse the last authorization URL for a friendlier display
  const parsedAuth = useMemo(() => {
    if (!lastUrl) return null;
    try {
      const u = new URL(lastUrl);
      const endpoint = `${u.origin}${u.pathname}`;
      const rawEntries = Array.from(u.searchParams.entries());
      const order = [
        'client_id',
        'redirect_uri',
        'scope',
        'response_type',
        'response_mode',
        'state',
        'nonce',
        'prompt',
        'code_challenge',
        'code_challenge_method',
        'login_hint',
        'domain_hint',
      ];
      const inOrder: Array<[string, string]> = [];
      const remaining: Array<[string, string]> = [];
      for (const e of rawEntries) {
        (order.includes(e[0]) ? inOrder : remaining).push(e);
      }
      // Keep known params in preferred order, then the rest alphabetically by name
      const sortedKnown = order
        .flatMap((k) => inOrder.filter(([name]) => name === k));
      const sortedRemaining = remaining.sort((a, b) => a[0].localeCompare(b[0]));
      const params = [...sortedKnown, ...sortedRemaining];
      return { endpoint, params };
    } catch {
      return null;
    }
  }, [lastUrl]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin) return;
      if (!e.data || (e.data as any).source !== 'raw-oauth-callback') return;

      const { payload } = e.data as { source: string; payload: Record<string, string> };
      
      // Check for OAuth errors first - show simple message and raw payload
      if (payload.error) {
        const msg = payload.error_description
          ? `${payload.error}: ${payload.error_description}`
          : String(payload.error);
        setError(msg);
        setResult(payload); // Still set result to show raw error response
        try {
          popupRef.current?.close();
        } catch {}
        return;
      }
      
      // Validate state/nonce when present
      if (payload.state && payload.state !== stateVal) {
        setError('State mismatch. Possible CSRF.');
        setResult(null);
        return;
      }
      if (payload.id_token) {
        const parts = payload.id_token.split('.');
        const idPayload = parts.length >= 2 ? decodeJwtPart(parts[1]) : null;
        if (idPayload && idPayload.nonce && idPayload.nonce !== nonceVal) {
          setError('Nonce mismatch in id_token.');
          setResult(null);
          return;
        }
      }

      setError('');
      setResult(payload);
      
      // For hybrid flow, extract authorization code
      if (payload.code) {
        setAuthCode(payload.code);
      }
      
      try {
        popupRef.current?.close();
      } catch {}
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, stateVal, nonceVal]);

  const launch = async (kind: FlowKind) => {
    setResult(null);
    setError('');
    setAuthCode('');
    setExchangeResult(null);

    const authBase = `https://login.microsoftonline.com/${form.tenant}`;
    const params: Record<string, string> = {
      __authBase__: authBase,
      client_id: form.clientId.trim(),
      redirect_uri: form.redirectUri.trim(),
      scope: form.scopes.trim(),
      response_type: kind === 'hybrid' ? 'code id_token' : 'token id_token',
      response_mode: 'fragment',
      state: stateVal,
      nonce: nonceVal,
      prompt: 'select_account',
    };

    // Add PKCE for hybrid flow
    if (kind === 'hybrid') {
      const verifier = generateCodeVerifier();
      const challenge = await generateCodeChallenge(verifier);
      setCodeVerifier(verifier);
      setCodeChallenge(challenge);
      params.code_challenge = challenge;
      params.code_challenge_method = 'S256';
    }

    if (!params.client_id || !params.redirect_uri) {
      setError('client_id and redirect_uri are required.');
      return;
    }

    const url = buildAuthorizeUrl(params);
    setLastUrl(url);

    const w = 480;
    const h = 700;
    const y = window.top ? Math.max(0, (window.top.outerHeight - h) / 2) : 0;
    const x = window.top ? Math.max(0, (window.top.outerWidth - w) / 2) : 0;

    popupRef.current = window.open(
      url,
      'raw-oauth',
      `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes`
    );

    if (!popupRef.current) {
      setError('Popup blocked. Allow popups for this site and try again.');
    }
  };

  const exchangeCode = async () => {
    if (!authCode || !codeVerifier) {
      setError('Missing authorization code or code verifier.');
      return;
    }

    setExchanging(true);
    setError('');
    setExchangeResult(null);

    try {
      const tokenEndpoint = `https://login.microsoftonline.com/${form.tenant}/oauth2/v2.0/token`;
      const body = new URLSearchParams({
        client_id: form.clientId.trim(),
        grant_type: 'authorization_code',
        code: authCode,
        redirect_uri: form.redirectUri.trim(),
        code_verifier: codeVerifier,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.error_description || data.error || 'Token exchange failed';
        setError(errorMsg);
        setExchangeResult({ error: true, ...data });
      } else {
        setExchangeResult(data);
      }
    } catch (err: any) {
      setError(`Exchange error: ${err.message}`);
      setExchangeResult({ error: true, message: err.message });
    } finally {
      setExchanging(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1200);
    } catch {}
  };

  const clearAll = () => {
    setForm({
      clientId: '',
      tenant: currentTenantId || 'organizations',
      redirectUri: defaultRedirectUri,
      scopes: defaultScopes,
    });
    setStateVal(randomString(16));
    setNonceVal(randomString(16));
    setResult(null);
    setError('');
    setLastUrl('');
    setFlowType('implicit');
    setCodeVerifier('');
    setCodeChallenge('');
    setAuthCode('');
    setExchangeResult(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Raw OAuth Tester
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Test OAuth 2.0 Implicit and Hybrid flows without MSAL library
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Configuration Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <VpnKeyIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Configuration</Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Flow Type Selector */}
            <Grid item xs={12}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: flowType === 'hybrid' ? '#f1f8f4' : '#fef7e0',
                  borderColor: flowType === 'hybrid' ? '#4caf50' : '#ff9800',
                  borderWidth: 1.5
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 600, color: 'text.primary' }}>OAuth Flow</InputLabel>
                  <Select
                    value={flowType}
                    label="OAuth Flow"
                    onChange={(e) => setFlowType(e.target.value as FlowKind)}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="implicit">Implicit Flow (response_type=token id_token)</MenuItem>
                    <MenuItem value="hybrid">Hybrid Flow with PKCE (response_type=code id_token)</MenuItem>
                  </Select>
                  <FormHelperText sx={{ mt: 1, fontSize: '0.8rem' }}>
                    {flowType === 'implicit' 
                      ? '⚠️ Legacy flow: Returns tokens directly in URL fragment. Less secure, no refresh tokens.'
                      : '⚡ Hybrid flow with PKCE: More secure than implicit, but not recommended for new SPAs. Use for testing/compatibility only.'}
                  </FormHelperText>
                </FormControl>
              </Paper>
            </Grid>

            {/* Client ID */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Client ID (Application ID)"
                value={form.clientId}
                onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
                fullWidth
                size="small"
                required
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' } }}
                helperText="The Application (client) ID from your Entra app registration"
              />
            </Grid>

            {/* Tenant Selector */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontWeight: 500, color: 'text.secondary' }}>Tenant</InputLabel>
                <Select
                  value={form.tenant}
                  label="Tenant"
                  onChange={(e) => setForm((s) => ({ ...s, tenant: e.target.value }))}
                >
                  {tenantOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Redirect URI */}
            <Grid item xs={12} md={8}>
              <TextField
                label="Redirect URI"
                value={form.redirectUri}
                onChange={(e) => setForm((s) => ({ ...s, redirectUri: e.target.value }))}
                fullWidth
                size="small"
                required
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' } }}
                helperText="Must be registered in your Entra app's redirect URIs"
              />
            </Grid>

            {/* Use Current Origin Button */}
            <Grid item xs={12} md={4}>
              <Tooltip title="Automatically sets the redirect URI to your current domain + /raw-oauth-callback.html. This callback page receives OAuth responses and sends them back to this tool.">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setForm((s) => ({ ...s, redirectUri: `${origin}/raw-oauth-callback.html` }))}
                  sx={{ height: 40, mt: 0.5 }}
                >
                  Use Current Origin
                </Button>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                Sets: {origin}/raw-oauth-callback.html
              </Typography>
            </Grid>

            {/* Scopes */}
            <Grid item xs={12} md={8}>
              <TextField
                label="Scopes"
                value={form.scopes}
                onChange={(e) => setForm((s) => ({ ...s, scopes: e.target.value }))}
                fullWidth
                size="small"
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' } }}
                helperText="Space-separated scopes (include 'openid profile' for id_token)"
              />
            </Grid>

            {/* Scope Presets */}
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setForm((s) => ({ ...s, scopes: 'openid profile' }))}
                >
                  Basic OpenID
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setForm((s) => ({ ...s, scopes: 'openid profile User.Read' }))}
                >
                  + User.Read
                </Button>
              </Stack>
            </Grid>

            {/* State and Nonce */}
            <Grid item xs={6} md={4}>
              <Tooltip 
                title="Auto-generated random value for CSRF protection. Use 'Regenerate Values' button to change instead of manual editing."
                arrow
                placement="top"
              >
                <TextField
                  label="state"
                  value={stateVal}
                  onChange={(e) => setStateVal(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' } }}
                  helperText="CSRF protection"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={6} md={4}>
              <Tooltip 
                title="Auto-generated random value for ID token replay protection. Use 'Regenerate Values' button to change instead of manual editing."
                arrow
                placement="top"
              >
                <TextField
                  label="nonce"
                  value={nonceVal}
                  onChange={(e) => setNonceVal(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' } }}
                  helperText="Replay protection"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => { setStateVal(randomString(16)); setNonceVal(randomString(16)); }}
                sx={{ height: 40 }}
              >
                Regenerate Values
              </Button>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button 
              variant="contained" 
              startIcon={<LockOpenIcon />} 
              onClick={() => launch(flowType)} 
              sx={{ borderRadius: 2, minWidth: 200 }}
              disabled={!form.clientId.trim() || !form.redirectUri.trim()}
            >
              {flowType === 'implicit' ? 'Run Implicit Flow' : 'Run Hybrid Flow'}
            </Button>
            <Button 
              variant="text" 
              startIcon={<ClearIcon />} 
              onClick={clearAll}
              sx={{ borderRadius: 2, color: 'text.secondary' }}
            >
              Clear All
            </Button>
          </Box>

          {/* Request URL Display */}
          {lastUrl && (
            <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: '#f8f9fa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Last Authorization Request
              </Typography>

              {parsedAuth && (
                <Box>
                  {/* Endpoint */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip label="Endpoint" size="small" variant="outlined" sx={{ mr: 1 }} />
                    <Box sx={{ flex: 1, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                      {parsedAuth.endpoint}
                    </Box>
                    <IconButton size="small" onClick={() => copy(parsedAuth.endpoint)}>
                      {copyOk ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Box>

                  {/* Parameters */}
                  <Typography variant="body2" sx={{ mt: 1, mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                    Parameters
                  </Typography>
                  <Stack spacing={0.75}>
                    {parsedAuth.params.map(([name, value], idx) => (
                      <Box key={`${name}-${idx}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ minWidth: 160 }}>
                          <Chip label={name} size="small" variant="outlined" />
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', bgcolor: '#ffffff', p: 0.5, borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
                          {value}
                        </Box>
                        <IconButton size="small" onClick={() => copy(value)} sx={{ ml: 0.5 }}>
                          {copyOk ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Raw URL fallback/copy */}
              <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Raw URL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {lastUrl}
                  </Box>
                  <IconButton size="small" onClick={() => copy(lastUrl)}>
                    {copyOk ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Results Card */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <AssignmentIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Response</Typography>
          </Box>

          {!result && !error && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <VpnKeyIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Ready to Test OAuth Flows
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure your parameters above and click "Run Implicit Flow" to begin testing.
              </Typography>
            </Box>
          )}

          {error && (
            <Paper variant="outlined" sx={{ p: 3, borderColor: 'error.light', bgcolor: '#fff5f5' }}>
              <Typography variant="subtitle1" color="error" sx={{ mb: 2, fontWeight: 600 }}>
                Authentication Error
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {error}
              </Typography>
            </Paper>
          )}

          {result && (
            <Stack spacing={3}>
              {(result.error || result.error_description) ? (
                <Paper variant="outlined" sx={{ p: 3, borderColor: 'error.light', bgcolor: '#fff5f5' }}>
                  <Typography variant="subtitle1" color="error" sx={{ mb: 2, fontWeight: 600 }}>
                    OAuth Error Response
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', color: 'error.main' }}>
                    {JSON.stringify(result, null, 2)}
                  </Box>
                </Paper>
              ) : (
                <>
                  {/* Response Summary */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      Response Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: authCode ? 2 : 0 }}>
                      {result.token_type && <Chip label={`Type: ${result.token_type}`} size="small" variant="outlined" />}
                      {result.expires_in && <Chip label={`Expires: ${result.expires_in}s`} size="small" variant="outlined" />}            
                      {result.scope && <Chip label={`Scope: ${result.scope.split(' ').length} permissions`} size="small" variant="outlined" />}
                      {authCode && <Chip label="Authorization Code" color="warning" size="small" />}
                      {result.access_token && <Chip label="Access Token" color="success" size="small" />}
                      {result.id_token && <Chip label="ID Token" color="primary" size="small" />}
                    </Box>

                    {/* Authorization Code Exchange for Hybrid Flow */}
                    {authCode && (
                      <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fff8e1', borderColor: '#ffc107' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip label="Step 2" color="warning" size="small" sx={{ mr: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Exchange Authorization Code for Tokens
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Step 1</strong> obtained the authorization code and ID token from <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>/authorize</code> endpoint.
                          Now in <strong>Step 2</strong>, exchange the code at <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>/token</code> endpoint 
                          for access token, refresh token, and a new ID token using PKCE verification.
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                          💡 The /token endpoint will issue a fresh ID token along with access and refresh tokens
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={exchangeCode}
                            disabled={exchanging}
                            size="small"
                            sx={{ borderRadius: 2 }}
                          >
                            {exchanging ? 'Exchanging...' : 'Exchange Code for Tokens'}
                          </Button>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            Code: {authCode.substring(0, 20)}...
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </Box>

                  {/* Token Details */}
                  <Grid container spacing={3}>
                    {result.id_token && (
                      <Grid item xs={12} lg={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                              <Chip label="ID Token" color="primary" size="small" sx={{ mr: 1 }} />
                              Decoded Claims
                            </Typography>
                            {authCode && (
                              <Chip label="/authorize" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            )}
                          </Box>
                          <Box sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.85rem', 
                            bgcolor: '#f8f9fa', 
                            p: 2, 
                            borderRadius: 1, 
                            maxHeight: 300, 
                            overflow: 'auto',
                            border: '1px solid #e0e0e0'
                          }}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {(() => {
                                const parts = String(result.id_token).split('.');
                                const payload = parts.length >= 2 ? decodeJwtPart(parts[1]) : null;
                                return JSON.stringify(payload, null, 2);
                              })()}
                            </pre>
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {result.access_token && (
                      <Grid item xs={12} lg={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                              <Chip label="Access Token" color="success" size="small" sx={{ mr: 1 }} />
                              Decoded Claims
                            </Typography>
                            {!authCode && (
                              <Chip label="/authorize" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            )}
                          </Box>
                          <Box sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.85rem', 
                            bgcolor: '#f8f9fa', 
                            p: 2, 
                            borderRadius: 1, 
                            maxHeight: 300, 
                            overflow: 'auto',
                            border: '1px solid #e0e0e0'
                          }}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {(() => {
                                const parts = String(result.access_token).split('.');
                                const payload = parts.length >= 2 ? decodeJwtPart(parts[1]) : null;
                                return payload ? JSON.stringify(payload, null, 2) : '(Opaque token - no JWT structure detected)';
                              })()}
                            </pre>
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                  </Grid>

                  {/* Token Exchange Result */}
                  {exchangeResult && (
                    <Paper variant="outlined" sx={{ p: 3, mt: 3, bgcolor: exchangeResult.error ? '#ffebee' : '#e8f5e9' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip 
                          label={exchangeResult.error ? 'Exchange Failed' : 'Exchange Successful'} 
                          color={exchangeResult.error ? 'error' : 'success'} 
                          size="small" 
                          sx={{ mr: 1 }} 
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Token Exchange Result
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                        📚 These tokens were obtained from the <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>/token</code> endpoint 
                        (vs. the initial code + ID token from <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>/authorize</code>)
                      </Typography>

                      {exchangeResult.error ? (
                        <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                          {exchangeResult.error_description || exchangeResult.message || 'Unknown error'}
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Access Token Claims
                                </Typography>
                                <Chip label="/token" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                              </Box>
                              <Box sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.75rem', 
                                maxHeight: 250, 
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {(() => {
                                  if (!exchangeResult.access_token) return 'No access token';
                                  const parts = String(exchangeResult.access_token).split('.');
                                  const payload = parts.length >= 2 ? decodeJwtPart(parts[1]) : null;
                                  return payload ? JSON.stringify(payload, null, 2) : '(Opaque token)';
                                })()}
                              </Box>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Token Metadata
                                </Typography>
                                <Chip label="/token" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {exchangeResult.token_type && (
                                  <Chip label={`Type: ${exchangeResult.token_type}`} size="small" variant="outlined" />
                                )}
                                {exchangeResult.expires_in && (
                                  <Chip label={`Expires in: ${exchangeResult.expires_in}s`} size="small" variant="outlined" />
                                )}
                                {exchangeResult.scope && (
                                  <Chip label={`Scopes: ${exchangeResult.scope.split(' ').length}`} size="small" variant="outlined" />
                                )}
                                {exchangeResult.refresh_token && (
                                  <Chip label="Refresh Token Included" color="success" size="small" />
                                )}
                                {exchangeResult.id_token && (
                                  <Chip label="New ID Token from /token" color="primary" size="small" />
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  )}

                  {/* Raw Response */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Raw Response Data
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem', 
                        bgcolor: '#fafafa', 
                        p: 2, 
                        borderRadius: 1, 
                        maxHeight: 400, 
                        overflow: 'auto',
                        border: '1px solid #e0e0e0'
                      }}>
                        {JSON.stringify(result, null, 2)}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, mt: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About Raw OAuth Testing
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                This tool allows you to test OAuth 2.0 flows directly without using MSAL.js, including legacy Implicit flow 
                and modern Hybrid flow with PKCE. Use this for testing authentication configurations and debugging.
              </Typography>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Supported Flows:
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Implicit" size="small" color="info" sx={{ mr: 1, minWidth: 90, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        response_type=token id_token - Returns tokens directly in URL fragment
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Legacy flow, less secure, no refresh tokens
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Hybrid" size="small" color="warning" sx={{ mr: 1, minWidth: 90, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        response_type=code id_token - Returns authorization code + id_token, then exchange code for tokens
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        More secure than implicit with PKCE, but exposes ID token in URL. Not recommended for new SPAs.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Prerequisites:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • Register the redirect URI (raw-oauth-callback.html) in your Entra app
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Enable "ID tokens" under Implicit grant and hybrid flows (required for both flows)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Enable "Access tokens" for Implicit flow only (not needed for Hybrid flow)
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                  ⚠️ Security Guidance:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Implicit flow</strong> is deprecated and insecure - tokens exposed in URL, no refresh tokens, vulnerable to leaks. Never use in production.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Hybrid flow</strong> is more secure than implicit (PKCE protects code exchange), but still exposes ID token in URL fragment. 
                  Not recommended for new applications.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  ✅ For production SPAs, use <strong>Authorization Code flow + PKCE</strong> (response_type=code only) as implemented by MSAL 2.0. 
                  This tool is for testing, debugging, and understanding OAuth flow mechanics.
                </Typography>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
};

export default RawOAuthTester;
